import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material';

import React, { ChangeEvent, useState, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import {
	postMessageFeedBackData,
	postModelFeedbackData,
	putMessageFeedBackData,
	putModelFeedbackData,
} from 'common/api';
import { IObjectData } from 'common/api/type-def';
import { ReactComponent as ExpandMoreRoundedIcon } from 'common/assets/images/select-chevron.svg';
import { ReactComponent as ThumbupFill } from 'common/assets/images/thumbup-fill.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { BUTTON_LABELS, MODULES } from 'common/constants';
import {
	FEEDBACK_FORM_LABEL,
	unLikeResponseOptions,
} from 'common/constants/AskRiskLinkConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { IFeedbackProps } from 'features/OpenRiskGPT/api/type-def';

import styles from './FeedbackForm.module.scss';

interface ICommonFeedbackFormProps {
	module: string;
	objectType?: string;
	messageId: string;
	feedbackData: IFeedbackProps;
	isLikeView: boolean;
	handleSubmitFeedback: any;
	handleCancelFeedback: any;
	objectData?: IObjectData;
}

const { OK } = HTTP_STATUS_CODES;

const { FEEDBACK_ERROR_MESSAGE } = FEEDBACK_FORM_LABEL;

const FeedbackForm: React.FC<ICommonFeedbackFormProps> = (props) => {
	const {
		isLikeView,
		handleCancelFeedback,
		handleSubmitFeedback,
		feedbackData,
		messageId,
		objectType,
		module,
		objectData,
	} = props;

	const [likeResponse, setLikeResponse] = useState<string | null>(
		feedbackData.isLike ? feedbackData.comment || null : null,
	);
	const [loading, setIsLoading] = useState<boolean>(false);
	const [isLike, setIsLike] = useState<boolean>(isLikeView);
	const [reason, setReason] = useState<string>(feedbackData.reason || '');
	const [unlikeResponse, setUnLikeResponse] = useState<string | null>(
		!feedbackData.isLike ? feedbackData.comment || null : null,
	);

	const {
		FEEDBACK_LIKE_DISPLAY_LABEL,
		FEEDBACK_LIKE_LABEL,
		FEEDBACK_UNLIKE_DISPLAY_LABEL,
		FEEDBACK_UNLIKE_DISPLAY_LABEL_YOU,
		FEEDBACK_LIKE_DISPLAY_LABEL_YOU,
		FEEDBACK_UNLIKE_LABEL,
		FEEDBACK_UNLIKE_REASON,
	} = FEEDBACK_FORM_LABEL;

	useEffect(() => {
		setIsLike(isLikeView);
	}, [isLikeView]);

	const { ASK_RISK_LINK } = MODULES;

	const dispatch = useDispatch();

	const titleTextFieldValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
		setLikeResponse(e.target.value);
	};

	const onUnlinkChange = (
		event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>,
	) => {
		setReason(event?.target?.value);
	};

	const unlikeTextFieldValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
		setUnLikeResponse(e.target.value);
	};

	const submitFeedback = async () => {
		const feedbackDataHeaders = isLike
			? { comment: null, is_like: true, reason: likeResponse }
			: { comment: reason, is_like: false, reason: unlikeResponse };

		const modelFeedbackDataHeaders = {
			...feedbackDataHeaders,
			object_data: objectData,
			object_type: objectType,
		};
		let responseFromApi: any;
		setIsLoading(true);

		if (module !== ASK_RISK_LINK) {
			if (feedbackData?.id) {
				responseFromApi = await putModelFeedbackData(
					feedbackData?.id,
					modelFeedbackDataHeaders,
				);
			} else {
				responseFromApi = await postModelFeedbackData(modelFeedbackDataHeaders);
			}
		} else if (feedbackData?.id) {
			responseFromApi = await putMessageFeedBackData(
				messageId,
				feedbackDataHeaders,
			);
		} else {
			responseFromApi = await postMessageFeedBackData(
				messageId,
				feedbackDataHeaders,
			);
		}
		const response = responseFromApi.responseBody;
		if (responseFromApi.statusCode === OK) {
			const feedbackInfo = {
				comment: response?.comment || null,
				id: response?.id || null,
				isLike: response?.isLike,
				reason: response?.reason || null,
				objectData: response.objectData,
				objectType,
			};
			setIsLike(response?.isLike === true);
			handleSubmitFeedback(feedbackInfo);
		} else {
			dispatch(
				setToastMessage({
					message: FEEDBACK_ERROR_MESSAGE,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsLoading(false);
	};

	return (
		<Grid
			container
			className={styles['feedback-form-container']}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
			data-testid="feedback-form-container"
		>
			{isLike ? (
				<>
					<Grid item sm={12} className={styles['feedback-label']}>
						{module !== ASK_RISK_LINK && (
							<ThumbupFill className={styles['risk-gpt-thumbup-fill-icon']} />
						)}
						{module !== ASK_RISK_LINK
							? `${FEEDBACK_LIKE_DISPLAY_LABEL_YOU}`
							: `${FEEDBACK_LIKE_DISPLAY_LABEL}:`}
					</Grid>
					<Grid item sm={12}>
						<TextFieldWithLabel
							type="text"
							name="likeResponse"
							size="small"
							showLabel
							isFilled
							label={FEEDBACK_LIKE_LABEL}
							fullWidth
							value={likeResponse}
							error={false}
							helperText=""
							onChange={titleTextFieldValueChanged}
							InputProps={{
								disableUnderline: true,
								inputProps: { 'data-testid': 'like-response-field' },
							}}
						/>
					</Grid>
				</>
			) : (
				<>
					<Grid item sm={12} className={styles['feedback-label']}>
						{module !== ASK_RISK_LINK && (
							<ThumbupFill className={styles['risk-gpt-thumbdown-fill-icon']} />
						)}
						{module !== ASK_RISK_LINK
							? `${FEEDBACK_UNLIKE_DISPLAY_LABEL_YOU}`
							: `${FEEDBACK_UNLIKE_DISPLAY_LABEL}:`}
					</Grid>
					<Grid item sm={12} className={styles['feedback-label']}>
						<FormControl
							variant="filled"
							className={styles['type-formcontrol']}
						>
							<InputLabel
								className={styles['type-label']}
								data-testid="type-label"
								id="select-reason-label"
							>
								{FEEDBACK_UNLIKE_REASON}
								<span className={styles['select-label-asterisk']}>*</span>
							</InputLabel>
							<Select
								labelId="select-reason-label"
								label="Select reason"
								name="unselect reason"
								id="select-reason"
								IconComponent={ExpandMoreRoundedIcon}
								required
								fullWidth
								className={styles['select-item']}
								inputProps={{
									'data-testid': 'select-unlike-reason',
								}}
								value={reason}
								onChange={onUnlinkChange}
							>
								{unLikeResponseOptions.map((ele) => (
									<MenuItem value={ele.value} key={ele.value}>
										{ele.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item sm={12} className={styles['feedback-label']}>
						<TextFieldWithLabel
							name="unselect reason"
							type="text"
							size="small"
							multiLine
							showLabel
							isFilled
							label={FEEDBACK_UNLIKE_LABEL}
							fullWidth
							error={false}
							helperText=""
							wordLimit={420}
							className={styles['txt-reason']}
							value={unlikeResponse}
							positionClass={styles['word-limit-position']}
							onChange={unlikeTextFieldValueChanged}
							parentClass={styles['word-txt-position']}
							InputProps={{
								disableUnderline: true,
								inputProps: { 'data-testid': 'unlike-response-field' },
							}}
						/>
					</Grid>
				</>
			)}
			{loading ? (
				<Loader show />
			) : (
				<Grid item sm={12} className={styles['feedback-buttons']} gap="10px">
					<ButtonComp
						btnType="secondary"
						compClass={styles['cancel-button']}
						clickHandler={handleCancelFeedback}
						data-testid="cancel-model"
					>
						{BUTTON_LABELS.CANCEL}
					</ButtonComp>
					<ButtonComp
						disabled={!!(!isLike && !reason.length)}
						btnType="primary"
						compClass={styles['submit-button']}
						clickHandler={submitFeedback}
						data-testid="submit-model"
					>
						{BUTTON_LABELS.SUBMIT}
					</ButtonComp>
				</Grid>
			)}
		</Grid>
	);
};

export default FeedbackForm;
